import { useCallback, useEffect, useMemo, useRef } from 'react'
import { uniqBy } from 'lodash'
import { useSelector } from 'react-redux'
import { selectSliderOverlayAssets, getLayersIndexes } from 'selectors'
import useTimelineVideoPlayerAssetsProps from './useTimelineVideoPlayerAssetsProps'
import useTimelineVideoPlayerAssetsRefs from './useTimelineVideoPlayerAssetsRefs'
import { VideoAsset } from '~/models/Asset'
import * as Actions from '~/actions'
import { useAction } from '~/hooks'
import { secondsToTimelineTime } from '~/Util'

const useTimelineVideoPlayerAssets = ({
  playingMediaAssets,
  backgroundAssets,
  composedMediaAssets,
  onMounted,
  onBufferingProgress,
  transitionVideoAsset,
  isPlaying,
  rect,
  restAggregatedPlayerProps,
}) => {
  const sliderOverlayAssets = useSelector(selectSliderOverlayAssets)
  const layersIndexes = useSelector(getLayersIndexes)
  const correctAssetDuration = useAction(Actions.layer.correctAssetDuration)
  const timelinePlayers = useRef(new Map())

  const addTimelinePlayers = useCallback((id, playerRef) => {
    if (!playerRef || timelinePlayers.current.has(id)) {
      return
    }
    timelinePlayers.current.set(id, playerRef)
    const onDurationChange = async event => {
      if (event?.target && !isNaN(event.target.duration)) {
        const duration = secondsToTimelineTime(event.target.duration)
        await correctAssetDuration(id, duration)
        event.target.removeEventListener('durationchange', onDurationChange)
      }
    }

    playerRef.addEventListener('durationchange', onDurationChange)
  }, [ correctAssetDuration ])

  useEffect(() => () => {
    const players = timelinePlayers.current
    if (players) {
      players.forEach(player => {
        if (player.dispose) player.dispose()
      })
      players.clear()
    }
  }, [ timelinePlayers ])

  const renderingAssets = useMemo(() => {
    const r = uniqBy(playingMediaAssets
      .concat(backgroundAssets)
      .concat(composedMediaAssets), a => a.id)
      .sort((a, b) => a.startTime - b.startTime)
    // console.log(`renderingAssets ids: ${r.map(a => a.id)}`)
    return r
  },
  [ playingMediaAssets, backgroundAssets, composedMediaAssets ])

  const overlayAssets = sliderOverlayAssets
    .concat(playingMediaAssets.filter(a => a instanceof VideoAsset))
    .filter(asset => asset.width !== 0 && asset.height !== 0)
    .sort((a, b) => layersIndexes.get(b.layerId) - layersIndexes.get(a.layerId))

  const { videoRefs } = useTimelineVideoPlayerAssetsRefs({ renderingAssets })

  const getTimelineAssetVideoPlayerProps = useTimelineVideoPlayerAssetsProps({
    playingMediaAssets,
    rect,
    onMounted,
    onBufferingProgress,
    transitionVideoAsset,
    composedMediaAssets,
    videoRefs,
    isPlaying,
    restAggregatedPlayerProps,
  })

  return {
    renderingAssets,
    videoRefs,
    getTimelineAssetVideoPlayerProps,
    overlayAssets,
    addTimelinePlayers,
  }
}

export default useTimelineVideoPlayerAssets
